<template>
  <div id="testList_slide" class="p-test-detail">
    <info-toast
      :isOpen="isOpenInfoToast"
      :message="infoToastMessage"
      @close="closeInfoToast"
    ></info-toast>
    <div
        v-if="cbtUnlicensedError"
        class="c-table-no-data"
    >
      <p class="c-parts-text-alert">
        <span class="u-text-bold">{{ cbtUnlicensedError }}</span>
      </p>
    </div>
    <div
        v-else
        class="p-test-narrowing"
    >
      <resettable-select-single
        v-if="cbtYearClassSelectOptions"
        :options="cbtYearClassSelectOptions.academic_sessions"
        v-model:selected="academicSessionId"
        :init-label="true"
        label-name="年度"
        :reset-option="true"
        @change="selectAcademicSessionOption"
      ></resettable-select-single>

      <resettable-select-single
        v-if="cbtYearClassSelectOptions"
        :options="cbtYearClassSelectOptions.academic_years[academicSessionId] || []"
        v-model:selected="academicYear"
        :init-label="true"
        label-name="学年"
        :reset-option="true"
        :is-disabled="!academicSessionId"
        @change="selectAcademicYearOption"
      ></resettable-select-single>

      <resettable-select-single
        v-if="cbtYearClassSelectOptions && cbtSubjectFieldSelectOptions"
        :options="cbtSubjectFieldSelectOptions"
        v-model:selected="subjectFieldId"
        :init-label="true"
        label-name="教科"
        :reset-option="true"
        :is-disabled="academicSessionId === '' || academicYear === ''"
        @change="selectSubjectFieldOption"
      ></resettable-select-single>

      <div class="p-test-narrowing-manual">
        <p class="p-test-narrowing-manual__text">
          <a href="https://www.meijitosho.co.jp/gakusan/manaviria/help/" target="_blank" rel="noopener noreferrer" class="c-parts-link">「先生用マニュアル」に記載の注意事項を事前に必ずご覧ください。</a>
        </p>
      </div>
    </div>

    <div
        v-if="cbtUnits?.cbt_units?.length && cbtClassroomStatuses"
        class="p-test-table-container"
    >
      <div class="c-table-explain -right">
        <copy-table-button :target-table="cbtScheduleTableRef" />
      </div>
      <div class="c-table-test-list-wrapper -scroll">
        <table
            ref="cbtScheduleTableRef"
            class="c-table-test-list"
        >
          <thead>
              <tr>
                <th class="title">項目</th>
                <th
                    v-for="classroom in cbtClassroomStatuses.header_classrooms"
                    :key="classroom.uuid"
                    class="name"
                >
                  {{ classroom.name }}
                </th>
                <th data-copy-ignore></th>
              </tr>
          </thead>
          <tbody>
            <tr v-for="cbtUnit in cbtUnits?.cbt_units" :key="cbtUnit.id">
              <td>
                <div class="title">
                  <a v-bind="openPreview(cbtUnit.id)" class="c-link u-text-ellipsis title-text">
                    <plain-text-view :markdownText="cbtUnit.title"/>
                  </a>
                  <div class="action-btn">
                    <a
                      v-bind="reportLinkAttrs(cbtUnit.id)"
                      class="c-link -score"
                      target="_blank"
                      data-copy-ignore
                    >成績</a>
                  </div>
                </div>
              </td>
              <td
                  v-for="(classroomStatus, index) in classroomStatusesByCbtUnitMap[cbtUnit.id] || []"
                  :key="classroomStatus.classroom_uuid"
                  class="name"
              >

                <!-- CBT実施ステータス: 未実施 -->
                <template v-if="classroomStatus.cbt_execution_status === CbtExecutionStatus.NOT_STARTED">
                  <a
                      class="c-link -pencil"
                      @click.prevent="startCbt(cbtUnit.id, classroomStatus.classroom_uuid)"
                  >開始</a>
                </template>

                <!-- CBT実施ステータス: 未実施以外 -->
                <template v-else>
                  <!--
                    FIXME: ひとまず、ざっくり cbt_execution_status ごとに name-upper-wrapper 単位で分岐させています。
                      必要に応じて、タグやCSSクラスなどで分岐してください。
                   -->
                  <!-- CBT実施ステータス: 終了未返却 -->
                  <div
                      v-if="classroomStatus.cbt_execution_status === CbtExecutionStatus.FINISHED_NOT_RETURNED"
                      class="name-upper-wrapper"
                  >
                    <div class="name-upper">
                      <div v-html="getCbtExecutionStatusHtml(classroomStatus.cbt_execution_status)"></div>

                      <!-- 未提出者が存在する場合 -->
                      <!-- FIXME: 終了返却済 と同じ内容なので、将来的にはひとまとめに出来ると良さそう -->
                      <template v-if="classroomStatus.unsubmitted_students && classroomStatus.unsubmitted_students.length > 0">
                        <button
                          type="button"
                          class="number -menu menu-button"
                          :data-menu="`menu${cbtUnit.id}-${index}-not-submitted`"
                        >
                          <span class="finished-number">
                            {{ classroomStatus.number_of_submitted_students }}
                          </span>/{{ classroomStatus.total_number_of_students }}(人)
                        </button>
                        <div class="c-table-context-menu -not-submitted" :id="`menu${cbtUnit.id}-${index}-not-submitted`">
                          <div class="c-table-context-menu-title">
                            未提出者
                          </div>
                          <ul class="c-table-context-menu-list">
                            <li v-for="student in classroomStatus.unsubmitted_students" :key="student.id">
                              <span class="name u-text-ellipsis">{{ student.number }} {{ student.name }}</span>
                              <span v-if="student.submission_status === CbtSubmissionStatus.UNANSWERED" class="c-parts-unanswered">未解答</span>
                            </li>
                          </ul>
                        </div>
                      </template>
                      <!-- 未提出者が存在しない場合 -->
                      <div v-else class="number">
                        <span class="finished-number">
                          {{ classroomStatus.number_of_submitted_students }}
                        </span>/{{ classroomStatus.total_number_of_students }}(人)
                      </div>
                    </div>
                    <!-- ミートボールメニューボタン -->
                    <button type="button" class="button menu-button" :data-menu="`menu${cbtUnit.id}-${index}`">
                      <img
                          src="../../../../djangoapp/assets/teacher/img/common/icon_menu.svg"
                          alt="menu"
                      >
                    </button>
                    <!-- ミートボールメニュー -->
                    <div class="c-table-context-menu" :id="`menu${cbtUnit.id}-${index}`">
                      <button
                        type="button"
                        class="c-table-context-menu-item -return"
                        @click="openCbtReturnModal(cbtUnit, classroomStatus, cbtClassroomStatuses.header_classrooms[index])"
                      >
                        <span class="c-table-context-menu-label">返却</span>
                      </button>
                      <button
                        type="button"
                        class="c-table-context-menu-item -start"
                        @click="startCbt(cbtUnit.id, classroomStatus.classroom_uuid)"
                      >
                        <span class="c-table-context-menu-label">再開</span>
                      </button>
                    </div>
                  </div>

                  <!-- CBT実施ステータス: 終了返却済 -->
                  <div
                      v-else-if="classroomStatus.cbt_execution_status === CbtExecutionStatus.FINISHED_RETURNED"
                      class="name-upper-wrapper"
                  >
                    <div class="name-upper">
                      <div v-html="getCbtExecutionStatusHtml(classroomStatus.cbt_execution_status)"></div>
                      <!-- 未提出者が存在する場合 -->
                      <!-- FIXME: 終了未返却 と同じ内容なので、将来的にはひとまとめに出来ると良さそう -->
                      <template v-if="classroomStatus.unsubmitted_students && classroomStatus.unsubmitted_students.length > 0">
                        <button
                          type="button"
                          class="number -menu menu-button"
                          :data-menu="`menu${cbtUnit.id}-${index}-not-submitted`"
                        >
                          <span class="finished-number">
                            {{ classroomStatus.number_of_submitted_students }}
                          </span>/{{ classroomStatus.total_number_of_students }}(人)
                        </button>
                        <div class="c-table-context-menu -not-submitted" :id="`menu${cbtUnit.id}-${index}-not-submitted`">
                          <div class="c-table-context-menu-title">
                            未提出者
                          </div>
                          <ul class="c-table-context-menu-list">
                            <li v-for="student in classroomStatus.unsubmitted_students" :key="student.id">
                              <span class="name u-text-ellipsis">{{ student.number }} {{ student.name }}</span>
                              <span v-if="student.submission_status === CbtSubmissionStatus.UNANSWERED" class="c-parts-unanswered">未解答</span>
                            </li>
                          </ul>
                        </div>
                      </template>
                      <!-- 未提出者が存在しない場合 -->
                      <div v-else class="number">
                        <span class="finished-number">
                          {{ classroomStatus.number_of_submitted_students }}
                        </span>/{{ classroomStatus.total_number_of_students }}(人)
                      </div>
                    </div>
                    <!-- ミートボールメニューボタン -->
                    <button type="button" class="button menu-button" :data-menu="`menu${cbtUnit.id}-${index}`">
                      <img
                          src="../../../../djangoapp/assets/teacher/img/common/icon_menu.svg"
                          alt="menu"
                      >
                    </button>
                    <!-- ミートボールメニュー -->
                    <div class="c-table-context-menu" :id="`menu${cbtUnit.id}-${index}`">
                      <button
                        type="button"
                        class="c-table-context-menu-item -start"
                        @click="startCbt(cbtUnit.id, classroomStatus.classroom_uuid)"
                      >
                        <span class="c-table-context-menu-label">再開</span>
                      </button>
                    </div>
                  </div>

                  <!-- CBT実施ステータス: 進行中 -->
                  <div
                      v-else-if="classroomStatus.cbt_execution_status === CbtExecutionStatus.IN_PROGRESS"
                      class="name-upper-wrapper"
                  >
                    <div class="name-upper">
                      <div v-html="getCbtExecutionStatusHtml(classroomStatus.cbt_execution_status)"></div>
                      <div class="number">
                        <span class="finished-number">
                          {{ classroomStatus.number_of_submitted_students }}
                        </span>/{{ classroomStatus.total_number_of_students }}(人)
                      </div>
                    </div>
                    <!-- ミートボールメニューボタン -->
                    <button type="button" class="button menu-button" :data-menu="`menu${cbtUnit.id}-${index}`">
                      <img
                          src="../../../../djangoapp/assets/teacher/img/common/icon_menu.svg"
                          alt="menu"
                      >
                    </button>
                    <!-- ミートボールメニュー -->
                    <div class="c-table-context-menu" :id="`menu${cbtUnit.id}-${index}`">
                      <button
                        type="button"
                        class="c-table-context-menu-item -finished"
                        @click="openCbtFinishModal(cbtUnit, classroomStatus, cbtClassroomStatuses.header_classrooms[index])"
                      >
                        <span class="c-table-context-menu-label">終了</span>
                      </button>
                    </div>
                  </div>

                  <div class="name-lower">
                    <span class="date">{{ formatCreatedAt(classroomStatus.created_at) }}～</span>
                  </div>
                </template>
              </td>

              <td></td>
            </tr>
            <tr
              class="last"
              data-copy-ignore
            >
              <td colspan="8"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- テスト終了モーダル -->
  <modal-base :is-open="showCbtFinishModal">
    <div
      class="closeLayer"
      @click="closeCbtFinishModal()"
    >
    </div>
    <div class="c-popup-body -alert">
      <div class="c-popup-body__inner">
        <div class="c-popup-body__container">
          <h2 class="c-popup-body__header -border">
            終了しますか？
          </h2>
          <div class="c-form-list-wrapper">
            <div class="c-form-list">
              <div class="c-form-list-title">教科</div>
              <div class="c-form-list-content">{{ selectedSubjectFieldName }}</div>
            </div>
            <div class="c-form-list">
              <div class="c-form-list-title">タイトル</div>
              <div class="c-form-list-content u-text-ellipsis">
                <plain-text-view v-if="selectedCbtUnit" :markdownText="selectedCbtUnit.title"/>
              </div>
            </div>
            <div class="c-form-list">
              <div class="c-form-list-title">学級</div>
              <div class="c-form-list-content">{{ selectedHeaderClassroom?.name }}</div>
            </div>
          </div>
          <div class="c-button -col2 -modal">
            <button
              class="c-button-blue"
              @click="finishCbt(selectedCbtUnit.id, selectedClassroomStatus.classroom_uuid)"
            >
              <span class="label">終了する</span>
            </button>
            <button
              class="c-button-white -cancel"
              @click="closeCbtFinishModal()"
            >
              <span class="label">キャンセル</span>
            </button>
          </div>
        </div>
        <span
          class="c-popup-body__close"
          @click="closeCbtFinishModal()"
        >
          <span class="c-popup-body__close-inner"></span>
        </span>
      </div>
    </div>
  </modal-base>

  <!-- テスト返却モーダル -->
  <modal-base :is-open="showCbtReturnModal">
    <div
      class="closeLayer"
      @click="closeCbtReturnModal()"
    >
    </div>
    <div class="c-popup-body -alert">
      <div class="c-popup-body__inner">
        <div class="c-popup-body__container">
          <h2 class="c-popup-body__header -border">
            返却しますか？
          </h2>
          <div class="c-form-list-wrapper">
            <div class="c-form-list">
              <div class="c-form-list-title">教科</div>
              <div class="c-form-list-content">{{ selectedSubjectFieldName }}</div>
            </div>
            <div class="c-form-list">
              <div class="c-form-list-title">タイトル</div>
              <div class="c-form-list-content u-text-ellipsis">
                <plain-text-view v-if="selectedCbtUnit" :markdownText="selectedCbtUnit.title"/>
              </div>
            </div>
            <div class="c-form-list">
              <div class="c-form-list-title">学級</div>
              <div class="c-form-list-content">{{ selectedHeaderClassroom?.name }}</div>
            </div>
            <div class="c-form-list">
              <div class="c-form-list-title">提出済み</div>
              <div v-if="allStudentsSubmitted" class="c-form-list-content">
                {{ selectedClassroomStatus?.number_of_submitted_students }} / {{ selectedClassroomStatus?.total_number_of_students }}(人)
              </div>
              <div v-else class="c-form-list-content">
                <span class="u-text-bold u-text-alert">{{ selectedClassroomStatus?.number_of_submitted_students }}</span> / {{ selectedClassroomStatus?.total_number_of_students }}(人)
              </div>
            </div>
          </div>
          <template v-if="allStudentsSubmitted">
            <p class="c-form-list-text u-text-alert">
              返却すると、児童/生徒は採点結果と各小問の正答を確認できるようになります。<br>
              この操作は取消できませんのでご注意ください。
            </p>
          </template>
          <template v-else>
            <div class="c-parts-warning-area">
              <p class="c-parts-warning u-text-center">未提出の児童/生徒がいます。</p>
              <p class="c-parts-warning-text">
                児童/生徒に提出を指示してください。
              </p>
              <table class="c-table-small-list">
                <tr v-for="student in selectedClassroomStatus.unsubmitted_students">
                  <td>{{ selectedHeaderClassroom?.name }}</td>
                  <td>{{ student.number }}</td>
                  <td class="name u-text-ellipsis">{{ student.name }}</td>
                  <td><span v-if="student.submission_status === CbtSubmissionStatus.UNANSWERED" class="c-parts-unanswered">未解答</span></td>
                </tr>
              </table>
              <label class="c-form-checkbox-area">
                <input type="checkbox" class="c-form-checkbox" v-model="returnOk">
                <span class="c-form-checkbox-label -large -large-type2">このまま返却する</span>
              </label>
            </div>
            <p class="c-form-list-text u-text-alert">
              返却すると、提出済の児童/生徒は採点結果と各小問の正答を確認できるようになります。<br>
              この操作は取消できませんのでご注意ください。<br>
              ※未提出の児童/生徒は提出後に確認できます。（返却後も提出は可能です）<br>
            </p>
          </template>
          <div class="c-button -col2 -modal">
            <button
              class="c-button-blue"
              :class="{ '-disabled': !returnOk }"
              @click="returnCbt(selectedCbtUnit.id, selectedClassroomStatus.classroom_uuid)"
            >
              <span class="label">返却する</span>
            </button>
            <button
              class="c-button-white -cancel"
              @click="closeCbtReturnModal()"
            >
              <span class="label">キャンセル</span>
            </button>
          </div>
        </div>
        <span
          class="c-popup-body__close"
          @click="closeCbtReturnModal()"
        >
          <span class="c-popup-body__close-inner"></span>
        </span>
      </div>
    </div>
  </modal-base>

  <error-modal></error-modal>

</template>

<script setup lang="ts">
  import { ref, computed, onMounted, h } from "vue";
  import { useCbtStore } from "@/store/teacher/cbt_store";
  import {
    ICbtExecutionResult,
    ICbtUnitsData,
    ICbtClassroomStatus,
    ICbtClassroomStatuses,
    ICbtYearClassSelectOptions,
    ISelectSingleOption,
    ICbtSubjectFieldSelectOption,
  } from "@/consts/interfaces";
  import { CbtExecutionStatus, CbtSubmissionStatus } from "@/consts/cbt";
  import { format, parseISO } from "date-fns";
  import { ja } from 'date-fns/locale';
  import { setupWatchForMenuEventListener } from "@/util/menu";

  const cbtStore = useCbtStore()

  const cbtScheduleTableRef = ref<HTMLTableElement | null>(null)

  // スナックバー関連の変数
  const isOpenInfoToast = ref(false)
  const infoToastMessage = ref("")
  // FIXME: ICbtExecutionResultは共通化する予定(NFR-6698)
  const cbtExecutionResult = ref<ICbtExecutionResult | null>(null);

  // スナックバーを表示
  function openInfoToast(message: string) {
    isOpenInfoToast.value = true
    infoToastMessage.value = message
  }

  // スナックバーを閉じる
  function closeInfoToast() {
    isOpenInfoToast.value = false
    infoToastMessage.value = ""
  }

  // CBT終了モーダル・CBT返却モーダル関連の変数
  const showCbtFinishModal = ref(false);
  const showCbtReturnModal = ref(false);
  const selectedSubjectFieldName = ref(null);
  const selectedCbtUnit = ref(null);
  const selectedClassroomStatus = ref(null);
  const selectedHeaderClassroom = ref(null);
  const allStudentsSubmitted = ref(true);
  const returnOk = ref(true);
  const openCbtFinishModal = (cbtUnit, classroomStatus, headerClassroom) => {
    selectedCbtUnit.value = cbtUnit;
    selectedClassroomStatus.value = classroomStatus;
    selectedHeaderClassroom.value = headerClassroom;
    showCbtFinishModal.value = true;
  };
  const closeCbtFinishModal = () => {
    selectedCbtUnit.value = null;
    selectedClassroomStatus.value = null;
    selectedHeaderClassroom.value = null;
    showCbtFinishModal.value = false;
  };
  const openCbtReturnModal = (cbtUnit, classroomStatus, headerClassroom) => {
    selectedCbtUnit.value = cbtUnit;
    selectedClassroomStatus.value = classroomStatus;
    selectedHeaderClassroom.value = headerClassroom;
    allStudentsSubmitted.value = (classroomStatus.unsubmitted_students.length === 0);  // true: 未提出者なし、false: 未提出者あり
    returnOk.value = allStudentsSubmitted.value;  // 未提出者ありのときは、「このまま返却する」チェックボックスを false にリセットする
    showCbtReturnModal.value = true;
  };
  const closeCbtReturnModal = () => {
    selectedCbtUnit.value = null;
    selectedClassroomStatus.value = null;
    selectedHeaderClassroom.value = null;
    allStudentsSubmitted.value = true;
    returnOk.value = true;
    showCbtReturnModal.value = false;
  };

  // ステータスラベルを更新
  async function updateStatusLabel() {
    const queryParams = {
      academic_session_id: Number(academicSessionId.value),
      academic_year: Number(academicYear.value),
      subject_field_id: Number(subjectFieldId.value)
    }
    cbtClassroomStatuses.value = await cbtStore.fetchCbtClassroomStatuses(queryParams);
  }

  // CBT開始/再開
  async function startCbt(cbtUnitId: number, classroomUuid: string) {
    cbtExecutionResult.value = await cbtStore.postCbtStart(
      {
        cbt_unit_id: cbtUnitId,
        classroom_uuid: classroomUuid,
      }
    )
    // ステータスラベルを更新して、スナックバーに完了メッセージを表示
    if (cbtExecutionResult.value) {
      await updateStatusLabel();
      openInfoToast(cbtExecutionResult.value.message)
    }
  }

  // CBT終了
  async function finishCbt(cbtUnitId: number, classroomUuid: string) {
    cbtExecutionResult.value = await cbtStore.postCbtFinish(
      {
        cbt_unit_id: cbtUnitId,
        classroom_uuid: classroomUuid,
      }
    )
    closeCbtFinishModal();
    // ステータスラベルを更新して、スナックバーに完了メッセージを表示
    if (cbtExecutionResult.value) {
      await updateStatusLabel();
      openInfoToast(cbtExecutionResult.value.message)
    }
  }

  // CBT返却
  async function returnCbt(cbtUnitId: number, classroomUuid: string) {
    cbtExecutionResult.value = await cbtStore.postCbtReturn(
      {
        cbt_unit_id: cbtUnitId,
        classroom_uuid: classroomUuid,
      }
    )
    closeCbtReturnModal()
    // ステータスラベルを更新して、スナックバーに完了メッセージを表示
    if (cbtExecutionResult.value) {
      await updateStatusLabel();
      openInfoToast(cbtExecutionResult.value.message)
    }
  }

  // 開始日時(2023-07-29T12:34:56.000000 の形式)を MM/DD(EEE) HH:mm の形式に整形
  const formatCreatedAt = (createdAt: string) => {
    // CBT未実施時はこのメソッドは呼ばれないため、createdAtのnullチェックはしていない
    const formatted = format(parseISO(createdAt), 'MM/dd(EEE) HH:mm', { locale: ja });
    return formatted;
  };

  // ユーザーに選択された検索条件 (年度、学年、教科分野)
  const academicSessionId = ref<number | string>('');
  const academicYear = ref<number | string>('');
  const subjectFieldId = ref<number | string>('');

  // 先生の学校にCBT教材ライセンスがないエラー
  const cbtUnlicensedError = ref<string>("");
  // 年度・学年セレクトボックスの選択肢群
  const cbtYearClassSelectOptions = ref<ICbtYearClassSelectOptions | null>(null);
  // 教科分野一覧（年度・学年選択時にAPIから取得）
  const cbtSubjectFields = ref<ICbtSubjectFieldSelectOption[] | null>(null);
  // CBT回一覧
  const cbtUnits = ref<ICbtUnitsData | null>(null);
  // CBT回ごとの各学級の実施状況
  const cbtClassroomStatuses = ref<ICbtClassroomStatuses | null>(null);

  // 教科分野セレクトボックスの選択肢群（セレクトボックス表示用に、has_license=true の教科のみ抽出）
  const cbtSubjectFieldSelectOptions = computed<ISelectSingleOption[]>(() => {
    if (!cbtSubjectFields.value) return [];

    return cbtSubjectFields.value
      .filter(option => option.has_license)
      .map(option => ({
        value: option.id,
        name: option.name
      }));
  });

  // 年度選択時
  const selectAcademicSessionOption = () => {
    academicYear.value = "";
    subjectFieldId.value = "";
    cbtSubjectFields.value = null;

    resetCbtScheduleList();
  };

  // 学年選択時
  const selectAcademicYearOption = async () => {
    subjectFieldId.value = "";

    resetCbtScheduleList();

    // 年度と学年が選択されている場合は教科一覧を取得
    if (academicSessionId.value !== "" && academicYear.value !== "") {
      cbtSubjectFields.value = await cbtStore.fetchCbtSubjectFields({
        academic_session_id: Number(academicSessionId.value),
        academic_year: Number(academicYear.value)
      }) ?? null;
    } else {
      cbtSubjectFields.value = null;
    }
  };

  // 教科分野選択時
  const selectSubjectFieldOption = () => {
    const selectedOption = cbtSubjectFieldSelectOptions.value.find(option => option.value === Number(subjectFieldId.value));
    if (selectedOption) {
      selectedSubjectFieldName.value = selectedOption.name;
      fetchCbtScheduleList();
    }
  };

  // 上位セレクトボックスクリア時
  const resetCbtScheduleList = () => {
    // 一覧表部分はクリア
    cbtUnits.value = null;
    cbtClassroomStatuses.value = null;
  };

  // CBT回ごとの各学級の実施状況変化時
  const classroomStatusesByCbtUnitMap = computed<Record<number, ICbtClassroomStatus[]>>(() => {
    // APIレスポンスの「CBT回ごとの各学級の実施状況」が更新されたタイミングで、
    // CBT回idをキーに、そのCBT回の各学級の実施状況をバリューに持つマップを再構築する
    const classroomStatuses = cbtClassroomStatuses.value
    if (!classroomStatuses) return {}

    const cbtUnitClassroomStatusesMap: Record<number, ICbtClassroomStatus[]> = {}

    for (const statusesByCbtUnit of classroomStatuses.classroom_statuses_per_cbt_unit) {
      cbtUnitClassroomStatusesMap[statusesByCbtUnit.cbt_unit_id] = statusesByCbtUnit.classroom_statuses
    }
    return cbtUnitClassroomStatusesMap
  })

  // CBT実施ステータスに応じたHTMLを返す
  // FIXME: <script>内にDOM操作を書くと責務分担が曖昧になるため、<template>内で完結させるよう、修正できると良さそう
  function getCbtExecutionStatusHtml(status: number): string {
    switch (status) {
      case CbtExecutionStatus.FINISHED_NOT_RETURNED:
        return `<div class="status -finished -unreturned">
                  <span class="small-text">終了</span><span class="unreturned">未返却</span>
                </div>`;
      case CbtExecutionStatus.FINISHED_RETURNED:
        return `<div class="status -finished">
                  <span class="small-text">終了</span><span>返却済</span>
                </div>`;
      case CbtExecutionStatus.IN_PROGRESS:
        return `<div class="status -progress">進行中</div>`;
      default:
        return "";
    }
  }

  // 初期表示時
  onMounted(async() => {
    // 年度・学年セレクトボックスの選択肢取得
    const response = await cbtStore.fetchCbtYearClass();

    if ("E005" === response?.code) {
      // エラー、かつ、404 (有効なCBT教材のライセンスがありません) の場合
      cbtUnlicensedError.value = response.title;
      return;
    }
    cbtYearClassSelectOptions.value = response;
  })

  // メニュー（ミートボールメニュー・未提出者リスト）関連のイベントリスナーが、
  // cbtClassroomStatuses の変更時に再設定されるように watch を設定
  setupWatchForMenuEventListener(cbtClassroomStatuses);

  // CBT回一覧、CBT回ごとの各学級の実施状況取得
  async function fetchCbtScheduleList() {
    const queryParams = {
      academic_session_id: Number(academicSessionId.value),
      academic_year: Number(academicYear.value),
      subject_field_id: Number(subjectFieldId.value)
    }

    cbtUnits.value = await cbtStore.fetchCbtUnits(queryParams);
    cbtClassroomStatuses.value = await cbtStore.fetchCbtClassroomStatuses(queryParams);
  }
  // NFR-6764: CBT回のタイトルをクリックしたときの挙動を、以下のように変更
  function openPreview(cbtUnitId: number) {
    const allSelected = (
      academicSessionId.value &&
      academicYear.value &&
      subjectFieldId.value
    );
    if (!allSelected) return {href: "#"};
    return {href: `/teacher/cbt_unit/${cbtUnitId}/preview/`, target: "_blank"};
  };
  // 「成績」リンクの属性
  // すべての検索条件 (年度、学年、教科分野) が選択中の場合のみ、リンク先URLが設定される
  function reportLinkAttrs(cbtUnitId: number) {
    const allSelected = (
      academicSessionId.value &&
      academicYear.value &&
      subjectFieldId.value
    );
    if (!allSelected) return {};

    const params = new URLSearchParams({
      academic_session_id: String(academicSessionId.value),
      academic_year: String(academicYear.value),
      subject_field_id: String(subjectFieldId.value),
      cbt_unit_id: String(cbtUnitId),
    });
    return {href: `/teacher/cbt/report/?${params.toString()}`};
  };
</script>

<style scoped>
</style>
